import { NewsStatus, ToolbarOption } from '@common/clients/api';
import { type ContextData } from '@common/defaults/ContextData';
import { getThemeConfigByContext, type ThemeConfig, useThemeConfig } from '@common/hooks/useThemeConfig';

export interface ArtikelThemeConfig {
    excludedArticleBlocks: ToolbarOption[];
    defaultStatus: NewsStatus;
    prefillInitialAuthor: boolean;
    richNextEditorSupport: RichNextEditorSupport;
}

export const footballRelatedArticleBlocks = [
    ToolbarOption.INSERT_MATCH_BLOCK,
    ToolbarOption.INSERT_MATCH_DETAIL_SECTION,
    ToolbarOption.INSERT_MATCH_PREDICTION_BLOCK,
    ToolbarOption.INSERT_PLAYER,
    ToolbarOption.INSERT_PLAYER_GRADE,
    ToolbarOption.INSERT_POULE,
    ToolbarOption.INSERT_STANDING_BLOCK,
];

const babybytesArticleBlocks = [
    ToolbarOption.INSERT_TAG_POPULAR_BLOCK,
    ToolbarOption.INSERT_TAG_SEARCH_BLOCK,
    ToolbarOption.INSERT_POSTCARD_BLOCK,
];

const brightArticleBlocks = [ToolbarOption.INSERT_WATT_WIZARD_BLOCK];

export const articleBlockOrder = [
    ToolbarOption.INSERT_GALLERY,
    ToolbarOption.INSERT_IMAGES,
    ToolbarOption.INSERT_MEDIA_BLOCK,
    ToolbarOption.INSERT_RELATED_BLOCK,
    ...footballRelatedArticleBlocks,
    ToolbarOption.INSERT_POLL,
    ToolbarOption.INSERT_LISTICLE_BLOCK,
    ToolbarOption.INSERT_PAYWALL,
    ToolbarOption.INSERT_QUOTE,
    ToolbarOption.INSERT_POST,
    ToolbarOption.INSERT_INSTAGRAM_POST,
    ToolbarOption.INSERT_TIK_TOK_BLOCK,
    ToolbarOption.INSERT_YOUTUBE_VIDEO,
    ToolbarOption.INSERT_IFRAME,
    ToolbarOption.INSERT_EXTERNAL_SNIPPET,
    ToolbarOption.INSERT_WATT_WIZARD_BLOCK,
    ToolbarOption.INSERT_JOBS_OVERVIEW,
    ToolbarOption.INSERT_TAG_LIST_BLOCK,
    ...babybytesArticleBlocks,
];

export enum RichNextEditorSupport {
    FULL,
    PARTIAL_HIDE_DEPRECATED_FIELDS,
    PARTIAL_SHOW_DEPRECATED_FIELDS,
}

export const THEME_CONFIG: ThemeConfig<ArtikelThemeConfig> = {
    defaults: {
        excludedArticleBlocks: [
            ...footballRelatedArticleBlocks,
            ...babybytesArticleBlocks,
            ...brightArticleBlocks,
        ],
        defaultStatus: NewsStatus.DRAFT,
        prefillInitialAuthor: true,
        richNextEditorSupport: RichNextEditorSupport.PARTIAL_HIDE_DEPRECATED_FIELDS,
    },
    byPlatform: {
        bb: {
            excludedArticleBlocks: [...footballRelatedArticleBlocks, ...brightArticleBlocks],
        },
        br: {
            excludedArticleBlocks: [...footballRelatedArticleBlocks, ...babybytesArticleBlocks],
        },
        vi: {
            excludedArticleBlocks: [...babybytesArticleBlocks, ...brightArticleBlocks],
            richNextEditorSupport: RichNextEditorSupport.FULL,
            prefillInitialAuthor: false,
        },
        vp: {
            //FIXME: INSERT_JOBS_OVERVIEW is temporarily disabled. On small some important blocks where outside of bounds. Proper fix next week.
            excludedArticleBlocks: [
                ...babybytesArticleBlocks,
                ...brightArticleBlocks,
                ToolbarOption.INSERT_JOBS_OVERVIEW,
                ToolbarOption.INSERT_PAYWALL,
            ],
            defaultStatus: NewsStatus.PUBLISHED,
            prefillInitialAuthor: false,
            richNextEditorSupport: RichNextEditorSupport.FULL,
        },
        vn: {
            excludedArticleBlocks: [
                ...footballRelatedArticleBlocks,
                ...babybytesArticleBlocks,
                ...brightArticleBlocks,
                ToolbarOption.INSERT_PAYWALL,
            ],
            defaultStatus: NewsStatus.PUBLISHED,
            prefillInitialAuthor: false,
            richNextEditorSupport: RichNextEditorSupport.FULL,
        },
    },
    byHost: {
        vpbe: {
            excludedArticleBlocks: [
                ...babybytesArticleBlocks,
                ...brightArticleBlocks,
                ToolbarOption.INSERT_JOBS_OVERVIEW,
                ToolbarOption.INSERT_PAYWALL,
                ToolbarOption.INSERT_PLAYER_GRADE,
            ],
        },
    },
} as const;

export const getArtikelThemeConfig = (contextData: ContextData) =>
    getThemeConfigByContext({ config: THEME_CONFIG, contextData });

export const useArtikelThemeConfig = () => useThemeConfig(THEME_CONFIG);
